// ─── storage.js — persistenza dati studenti su Supabase ──────────────────────
//
// Strategia "local-first":
//   1. Il localStorage rimane la cache locale (lettura istantanea, offline support)
//   2. Supabase è il backend di verità (sync multi-device)
//   3. Al login: carica da Supabase; se Supabase è vuoto, verifica localStorage
//   4. Ad ogni modifica: salva su localStorage SUBITO + su Supabase con debounce
//
// Le letture ritornano null in caso di errore, così l'app continua a funzionare
// anche se Supabase non è raggiungibile. I salvataggi in lotto (studenti,
// scadenze, dati PEI) invece RILANCIANO l'errore: è AppContent a decidere cosa
// mostrare all'utente (banner di sincronizzazione non riuscita).

import { supabase, SUPABASE_ENABLED } from './supabase';

// ─── Helpers interni ──────────────────────────────────────────────────────────

/**
 * Converte un oggetto studente (formato app) nel formato riga DB.
 * I campi indicizzati vanno in colonne dedicate; il resto in `dati` JSONB.
 */
function studenteToRow(userId, studente) {
  const { id, iniziali, ordine, ...resto } = studente;
  // `documenti` viene gestito separatamente — non va in `dati`
  const { documenti: _docs, ...datiStudente } = resto;
  return {
    id:      String(id),
    user_id: userId,
    iniziali: iniziali || '?',
    ordine:   ordine   || 'primaria',
    dati:     datiStudente,   // eta, classe, diagnosi, icd10, note, colore, avatar, anno_scolastico, storicoPEI, …
    updated_at: new Date().toISOString(),
  };
}

/**
 * Ricostruisce un oggetto studente (formato app) da una riga DB.
 */
function rowToStudente(row) {
  return {
    id:      row.id,
    iniziali: row.iniziali,
    ordine:   row.ordine,
    ...(row.dati || {}),
  };
}


// ─── CARICA (al login) ────────────────────────────────────────────────────────

/**
 * Carica tutti i dati dell'utente da Supabase.
 * Ritorna { studenti, peiDatiMap, scadenze, documentiStudenti }
 * oppure null se Supabase non è disponibile o si verifica un errore.
 */
export async function caricaDaSupabase(userId) {
  if (!SUPABASE_ENABLED || !userId) return null;

  try {
    const [studentiRes, scadenzeRes, peiRes] = await Promise.all([
      supabase
        .from('students')
        .select('id, iniziali, ordine, dati, documenti')
        .eq('user_id', userId)
        .order('created_at', { ascending: true }),
      supabase
        .from('deadlines')
        .select('id, testo, data, tipo, urgente')
        .eq('user_id', userId)
        .order('created_at', { ascending: true }),
      supabase
        .from('pei_data')
        .select('student_id, dati')
        .eq('user_id', userId),
    ]);

    if (studentiRes.error) throw studentiRes.error;
    if (scadenzeRes.error) throw scadenzeRes.error;
    if (peiRes.error)      throw peiRes.error;

    // Ricostruisce array studenti
    const studenti = (studentiRes.data || []).map(rowToStudente);

    // Ricostruisce peiDatiMap: { [studentId]: { campo1: valore1, … } }
    const peiDatiMap = {};
    (peiRes.data || []).forEach(row => {
      peiDatiMap[row.student_id] = row.dati || {};
    });

    // Ricostruisce scadenze
    const scadenze = (scadenzeRes.data || []).map(row => ({
      id:      row.id,
      testo:   row.testo,
      data:    row.data    || '',
      tipo:    row.tipo    || 'altro',
      urgente: row.urgente || false,
    }));

    // Ricostruisce documentiStudenti: { [studentId]: [...docs] }
    const documentiStudenti = {};
    (studentiRes.data || []).forEach(row => {
      const docs = row.documenti;
      if (Array.isArray(docs) && docs.length > 0) {
        documentiStudenti[row.id] = docs;
      }
    });

    return { studenti, peiDatiMap, scadenze, documentiStudenti };

  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[storage] caricaDaSupabase error:', err);
    return null;
  }
}


// ─── SALVA STUDENTI ───────────────────────────────────────────────────────────

/**
 * Upsert dell'intero array studenti su Supabase.
 * Gestisce anche la lista degli ID da eliminare (studenti rimossi dall'app).
 *
 * @param {string}   userId
 * @param {Array}    studenti        - array corrente degli studenti nell'app
 * @param {Object}   documentiStudenti - mappa { [studentId]: [...docs] }
 */
export async function salvaStudenti(userId, studenti, documentiStudenti = {}) {
  if (!SUPABASE_ENABLED || !userId) return;

  try {
    if (studenti.length === 0) {
      // Se non ci sono studenti, elimina tutto per questo utente
      await supabase.from('students').delete().eq('user_id', userId);
      return;
    }

    const rows = studenti.map(s => ({
      ...studenteToRow(userId, s),
      documenti: documentiStudenti[s.id] || [],
    }));

    const { error } = await supabase
      .from('students')
      .upsert(rows, { onConflict: 'id' });

    if (error) throw error;

    // Elimina righe di studenti non più presenti nell'app
    const idAttivi = studenti.map(s => String(s.id));
    await supabase
      .from('students')
      .delete()
      .eq('user_id', userId)
      .not('id', 'in', `(${idAttivi.join(',')})`);

  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[storage] salvaStudenti error:', err);
    throw err;
  }
}


// ─── SALVA DATI PEI ───────────────────────────────────────────────────────────

/**
 * Upsert dei dati PEI di un singolo studente.
 * Chiamata su ogni modifica di peiDatiMap (debounced in AppContent).
 */
export async function salvaPeiDati(studentId, userId, dati) {
  if (!SUPABASE_ENABLED || !userId || !studentId) return;

  try {
    const { error } = await supabase
      .from('pei_data')
      .upsert(
        {
          student_id: String(studentId),
          user_id:    userId,
          dati:       dati || {},
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'student_id' }
      );

    if (error) throw error;

  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[storage] salvaPeiDati error:', err);
  }
}

/**
 * Salva l'intera peiDatiMap (tutte le chiavi) in un batch.
 * Più efficiente quando si importa un backup o si migra da localStorage.
 */
export async function salvaTuttaPeiDatiMap(userId, peiDatiMap) {
  if (!SUPABASE_ENABLED || !userId) return;

  try {
    const rows = Object.entries(peiDatiMap).map(([studentId, dati]) => ({
      student_id: String(studentId),
      user_id:    userId,
      dati:       dati || {},
      updated_at: new Date().toISOString(),
    }));

    if (rows.length === 0) return;

    const { error } = await supabase
      .from('pei_data')
      .upsert(rows, { onConflict: 'student_id' });

    if (error) throw error;

  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[storage] salvaTuttaPeiDatiMap error:', err);
    throw err;
  }
}


// ─── SALVA SCADENZE ───────────────────────────────────────────────────────────

/**
 * Sincronizza le scadenze: upsert delle righe correnti + eliminazione di quelle rimosse.
 */
export async function salvaScadenze(userId, scadenze) {
  if (!SUPABASE_ENABLED || !userId) return;

  try {
    if (scadenze.length === 0) {
      await supabase.from('deadlines').delete().eq('user_id', userId);
      return;
    }

    const rows = scadenze.map(s => ({
      id:      String(s.id),
      user_id: userId,
      testo:   s.testo   || '',
      data:    s.data    || null,
      tipo:    s.tipo    || 'altro',
      urgente: s.urgente || false,
    }));

    const { error } = await supabase
      .from('deadlines')
      .upsert(rows, { onConflict: 'user_id,id' });

    if (error) throw error;

    // Elimina scadenze rimosse
    const idAttivi = scadenze.map(s => String(s.id));
    await supabase
      .from('deadlines')
      .delete()
      .eq('user_id', userId)
      .not('id', 'in', `(${idAttivi.join(',')})`);

  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[storage] salvaScadenze error:', err);
    throw err;
  }
}


// ─── SALVA DOCUMENTI (per singolo studente) ───────────────────────────────────

/**
 * Aggiorna la colonna `documenti` di uno studente specifico.
 * Usa upsert così funziona anche se la riga non esiste ancora
 * (es. studente appena creato, non ancora sincronizzato su Supabase).
 */
export async function salvaDocumentiStudente(studentId, userId, documenti) {
  if (!SUPABASE_ENABLED || !userId || !studentId) return;

  try {
    // Prima prova update (più leggero, non richiede tutti i campi obbligatori)
    const { error: updateErr, count } = await supabase
      .from('students')
      .update({ documenti: documenti || [], updated_at: new Date().toISOString() })
      .eq('id', String(studentId))
      .eq('user_id', userId)
      .select('id', { count: 'exact', head: true });

    // Se la riga non esiste ancora (count = 0), fa nulla — il batch save
    // di salvaStudenti creerà la riga completa con i documenti inclusi
    if (updateErr) throw updateErr;

  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[storage] salvaDocumentiStudente error:', err);
  }
}


// ─── ELIMINA STUDENTE ─────────────────────────────────────────────────────────

/**
 * Elimina uno studente (e i suoi dati PEI via CASCADE) da Supabase.
 */
export async function eliminaStudenteDaSupabase(studentId, userId) {
  if (!SUPABASE_ENABLED || !userId || !studentId) return;

  try {
    const { error } = await supabase
      .from('students')
      .delete()
      .eq('id', String(studentId))
      .eq('user_id', userId);

    if (error) throw error;

  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[storage] eliminaStudenteDaSupabase error:', err);
  }
}


// ─── MIGRAZIONE localStorage → Supabase ──────────────────────────────────────

/**
 * Verifica se ci sono dati in localStorage da migrare su Supabase.
 * Ritorna i dati del localStorage se presenti e non ancora migrati, null altrimenti.
 *
 * Chiamata DOPO caricaDaSupabase: se Supabase è vuoto (0 studenti)
 * ma localStorage ha dati, offre la migrazione all'utente.
 */
export function leggiDatiLocalStorage(lsKey) {
  try {
    const raw = localStorage.getItem(lsKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Considera "con dati" solo se ci sono studenti
    if (!parsed?.studenti?.length) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Carica tutti i dati da localStorage + li carica su Supabase.
 * Da chiamare quando l'utente conferma la migrazione.
 *
 * @returns {boolean} true se la migrazione è avvenuta con successo
 */
export async function migraLocalStorageASupabase(userId, datiLS) {
  if (!SUPABASE_ENABLED || !userId || !datiLS) return false;

  try {
    const { studenti = [], peiDatiMap = {}, scadenze = [], documentiStudenti = {} } = datiLS;

    // Salva tutto in parallelo
    await Promise.all([
      salvaStudenti(userId, studenti, documentiStudenti),
      salvaTuttaPeiDatiMap(userId, peiDatiMap),
      salvaScadenze(userId, scadenze),
    ]);

    return true;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[storage] migrazione localStorage→Supabase error:', err);
    return false;
  }
}
